/**
 * Link in Bio always persists immediately (classic dashboard + website LiB page).
 * Website template pages (Home / Products / Blog / …) stay Publish-gated via
 * `websiteTemplateComponentSession`.
 *
 * This module still heals leftover `pending_publish` soft-adds from the old
 * LiB publish gate and keeps discard/publish helpers as no-op-safe cleanup.
 */

import { supabase } from "../../../config/supabaseClient";
import { markLinkInBioContentEdited } from "../GettingStarted/gettingStartedProgress";

export const MTE_LIB_SESSION_DISCARD = "mte-lib-session-discard";
export const MTE_LIB_SESSION_PUBLISHED = "mte-lib-session-published";

/** Soft-add flag for LiB publish gate — must not collide with classic `draft`. */
export const LIB_PENDING_PUBLISH_STATUS = "pending_publish";

const createdIds = new Set<string>();
const deletedIds = new Set<string>();

let lastShowing: any[] = [];
let lastArchive: any[] = [];
let applyLocalPatch: ((id: string, patch: Record<string, unknown>) => void) | null =
  null;

let gateEnabled = false;
let sessionDirty = false;
let orderDirty = false;
const dirtyIds = new Set<string>();
let onDirty: (() => void) | null = null;
let storageUserId = "";

function rowId(value: unknown): string {
  return value == null ? "" : String(value).trim();
}

function emit(name: string) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(name));
}

function storageKey(userId: string): string {
  return `mte-lib-session:${String(userId || "").trim()}`;
}

function persistSession() {
  if (typeof window === "undefined" || !storageUserId) return;
  try {
    window.sessionStorage.setItem(
      storageKey(storageUserId),
      JSON.stringify({
        created: [...createdIds],
        deleted: [...deletedIds],
        dirty: [...dirtyIds],
        orderDirty,
        sessionDirty,
      }),
    );
  } catch {
    /* private mode / quota — in-memory still works for the tab */
  }
}

function loadSession(userId: string) {
  storageUserId = String(userId || "").trim();
  createdIds.clear();
  deletedIds.clear();
  dirtyIds.clear();
  sessionDirty = false;
  orderDirty = false;
  if (typeof window === "undefined" || !storageUserId) return;
  try {
    const raw = window.sessionStorage.getItem(storageKey(storageUserId));
    if (!raw) return;
    const parsed = JSON.parse(raw) as {
      created?: unknown[];
      deleted?: unknown[];
      dirty?: unknown[];
      orderDirty?: boolean;
      sessionDirty?: boolean;
    };
    for (const id of parsed.created || []) {
      const key = rowId(id);
      if (key) createdIds.add(key);
    }
    for (const id of parsed.deleted || []) {
      const key = rowId(id);
      if (key) deletedIds.add(key);
    }
    for (const id of parsed.dirty || []) {
      const key = rowId(id);
      if (key) dirtyIds.add(key);
    }
    orderDirty = Boolean(parsed.orderDirty);
    sessionDirty =
      Boolean(parsed.sessionDirty) ||
      createdIds.size > 0 ||
      deletedIds.size > 0 ||
      dirtyIds.size > 0 ||
      orderDirty;
  } catch {
    /* ignore corrupt storage */
  }
}

function clearStorage() {
  if (typeof window === "undefined" || !storageUserId) return;
  try {
    window.sessionStorage.removeItem(storageKey(storageUserId));
  } catch {
    /* ignore */
  }
}

/** Bind / hydrate session tracking for this editor user. */
export function bindWebsiteLibSession(userId: string) {
  const uid = String(userId || "").trim();
  if (!uid) return;
  if (uid !== storageUserId) loadSession(uid);
  else storageUserId = uid;
}

export function beginWebsiteLibPersistGate(_cb: () => void) {
  // LiB writes immediately — never arm the defer gate.
  gateEnabled = false;
  onDirty = null;
}

export function endWebsiteLibPersistGate() {
  gateEnabled = false;
  onDirty = null;
  applyLocalPatch = null;
  lastShowing = [];
  lastArchive = [];
}

export function isWebsiteLibPersistGateEnabled(): boolean {
  return gateEnabled;
}

export function isWebsiteLibSessionDirty(): boolean {
  return sessionDirty;
}

export function markWebsiteLibDirty(id?: unknown) {
  if (!gateEnabled) return;
  sessionDirty = true;
  const key = rowId(id);
  if (key) dirtyIds.add(key);
  else orderDirty = true;
  persistSession();
  onDirty?.();
  void markLinkInBioContentEdited(storageUserId, null, "landing-page");
}

export function setWebsiteLibLocalPatcher(
  fn: ((id: string, patch: Record<string, unknown>) => void) | null,
) {
  applyLocalPatch = fn;
}

export function applyWebsiteLibLocalPatch(
  id: unknown,
  patch: Record<string, unknown>,
) {
  const key = rowId(id);
  if (!key) return;
  lastShowing = lastShowing.map((row) =>
    rowId(row?.id) === key ? { ...row, ...patch } : row,
  );
  lastArchive = lastArchive.map((row) =>
    rowId(row?.id) === key ? { ...row, ...patch } : row,
  );
  applyLocalPatch?.(key, patch);
  markWebsiteLibDirty(key);
}

export function rememberWebsiteLibStack(showing: any[], archive?: any[]) {
  if (Array.isArray(showing)) lastShowing = showing;
  if (Array.isArray(archive)) lastArchive = archive;
}

export function getWebsiteLibSessionStack(): { showing: any[]; archive: any[] } {
  return {
    showing: lastShowing,
    archive: lastArchive,
  };
}

/** Always false — Link in Bio must go live without Publish. */
export function isWebsiteLibPersistGated(_from?: string | null): boolean {
  return false;
}

/** Session soft-adds — invisible publicly via pending_publish. */
export function isWebsiteLibSessionCreated(id: unknown): boolean {
  const key = rowId(id);
  return Boolean(key) && createdIds.has(key);
}

export function getWebsiteLibCreatedIds(): string[] {
  return [...createdIds];
}

export function getWebsiteLibDeletedIds(): string[] {
  return [...deletedIds];
}

export function isWebsiteLibSessionDeleted(id: unknown): boolean {
  const key = rowId(id);
  return Boolean(key) && deletedIds.has(key);
}

export function isLibPendingPublishStatus(status: unknown): boolean {
  return (
    String(status || "")
      .toLowerCase()
      .trim() === LIB_PENDING_PUBLISH_STATUS
  );
}

/** True when a stay row is a LiB soft-add awaiting Publish. */
export function isLibPendingPublishComponent(row: any): boolean {
  const status = String(row?.status_expiry ?? row?.status ?? "")
    .toLowerCase()
    .trim();
  return isLibPendingPublishStatus(status);
}

export function shouldDeferWebsiteLibWrite(
  from?: string | null,
  componentId?: unknown,
): boolean {
  if (!isWebsiteLibPersistGated(from)) return false;
  if (isWebsiteLibSessionCreated(componentId)) return false;
  return true;
}

export function noteWebsiteLibCreatedId(id: unknown) {
  const key = rowId(id);
  if (!key) return;
  createdIds.add(key);
  deletedIds.delete(key);
  markWebsiteLibDirty(key);
}

export function noteWebsiteLibDeletedId(id: unknown) {
  const key = rowId(id);
  if (!key) return;
  if (createdIds.has(key)) {
    createdIds.delete(key);
    dirtyIds.delete(key);
    persistSession();
    markWebsiteLibDirty();
    return;
  }
  deletedIds.add(key);
  dirtyIds.delete(key);
  // Always persist tombstones — even when the publish gate is off — so stale
  // landing-stay refetches cannot resurrect a just-removed row in the editor.
  persistSession();
  markWebsiteLibDirty(key);
}

/** Drop a delete tombstone once the server no longer returns the row. */
export function clearWebsiteLibDeletedId(id: unknown) {
  const key = rowId(id);
  if (!key || !deletedIds.has(key)) return;
  deletedIds.delete(key);
  persistSession();
}

const SYNC_KEYS = [
  "name",
  "display_name",
  "details",
  "query",
  "post_ids",
  "custom_order",
  "content_order",
  "content_shape",
  "content_sound",
  "shoppable_content_filter",
  "shoppable_status_filter",
  "cta_placeholder",
  "original_banner_image",
  "removed_postids",
  "pinned_posts",
  "route_name",
  "unique_id",
  "start_date",
  "archive_date",
  "image_crop",
] as const;

function libStatus(row: Record<string, unknown> | null | undefined): string {
  return String(row?.status_expiry ?? row?.status ?? "active")
    .toLowerCase()
    .trim();
}

function payloadFromRow(
  row: Record<string, unknown>,
  index: number,
  status: string,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    index,
    status,
    status_expiry: status,
  };
  for (const key of SYNC_KEYS) {
    if (key in row) payload[key] = row[key];
  }
  return payload;
}

async function updateLibRow(
  userId: string,
  id: string,
  payload: Record<string, unknown>,
) {
  const { error } = await supabase
    .from("stayuptodate_components")
    .update(payload)
    .eq("id", id)
    .eq("user_id", userId)
    .neq("status", "deleted");
  if (error) throw error;
}

async function hardDeleteLibRows(userId: string, ids: string[]) {
  const uid = String(userId || "").trim();
  const clean = ids.map(rowId).filter(Boolean);
  if (!uid || !clean.length) return;
  await supabase
    .from("stayuptodate_components")
    .delete()
    .eq("user_id", uid)
    .in("id", clean);
}

async function lookupCreatedRowId(
  userId: string,
  name: string,
  type: string,
): Promise<string> {
  const { data } = await supabase
    .from("stayuptodate_components")
    .select("id")
    .eq("user_id", userId)
    .eq("name", name)
    .eq("type", type)
    .neq("status", "deleted")
    .order("index", { ascending: false })
    .limit(1);
  return rowId(data?.[0]?.id);
}

export async function noteWebsiteLibCreatedRow(opts: {
  userId: string;
  name: string;
  type: string;
  id?: unknown;
}) {
  const existing = rowId(opts.id);
  if (existing) {
    noteWebsiteLibCreatedId(existing);
    return;
  }
  const found = await lookupCreatedRowId(opts.userId, opts.name, opts.type);
  if (found) noteWebsiteLibCreatedId(found);
}

/** Hard-delete one session soft-add (Remove before Publish). */
export async function hardDeleteWebsiteLibSessionCreatedRow(
  userId: string,
  componentId: unknown,
) {
  const uid = String(userId || "").trim();
  const id = rowId(componentId);
  if (!uid || !id) return;
  await hardDeleteLibRows(uid, [id]);
  createdIds.delete(id);
  deletedIds.delete(id);
  dirtyIds.delete(id);
  persistSession();
  markWebsiteLibDirty();
}

/**
 * Promote leftover `pending_publish` LiB rows (from the old publish gate) to
 * `active` so they show on the public Link in Bio immediately.
 */
export async function healPendingPublishLibToActive(userId: string) {
  const uid = String(userId || "").trim();
  if (!uid) return;
  const { error } = await supabase
    .from("stayuptodate_components")
    .update({
      status: "active",
      status_expiry: "active",
    })
    .eq("user_id", uid)
    .eq("status", LIB_PENDING_PUBLISH_STATUS);
  if (error) {
    console.error("healPendingPublishLibToActive:", error.message);
  }
}

/**
 * Clear LiB session tracking only. Soft-adds are already live, so Discard /
 * reload must not hard-delete Link in Bio components.
 */
export async function discardWebsiteLibSession(userId: string) {
  bindWebsiteLibSession(userId);
  createdIds.clear();
  deletedIds.clear();
  dirtyIds.clear();
  sessionDirty = false;
  orderDirty = false;
  lastShowing = [];
  lastArchive = [];
  clearStorage();
  emit(MTE_LIB_SESSION_DISCARD);
}

/**
 * On editor mount: promote any leftover pending_publish rows, then clear
 * stale session tracking from the old LiB publish gate.
 */
export async function maybeAbandonWebsiteLibSessionOnLoad(userId: string) {
  bindWebsiteLibSession(userId);
  await healPendingPublishLibToActive(userId);
  createdIds.clear();
  deletedIds.clear();
  dirtyIds.clear();
  sessionDirty = false;
  orderDirty = false;
  clearStorage();
}

/**
 * Publish: activate soft-adds (preserve draft/archive/schedule if set),
 * flush dirty edits, hard-delete deferred soft-removes.
 */
export async function publishWebsiteLibFeed(opts: {
  userId: string;
  showing?: any[];
  archive?: any[];
}): Promise<boolean> {
  const userId = String(opts.userId || "").trim();
  if (!userId) return false;
  // Safety: promote any leftover pending_publish rows (old LiB gate).
  await healPendingPublishLibToActive(userId);
  bindWebsiteLibSession(userId);

  const deleted = [...deletedIds];
  if (!sessionDirty && createdIds.size === 0 && deleted.length === 0) {
    createdIds.clear();
    deletedIds.clear();
    dirtyIds.clear();
    sessionDirty = false;
    orderDirty = false;
    clearStorage();
    emit(MTE_LIB_SESSION_PUBLISHED);
    return true;
  }

  const remembered = getWebsiteLibSessionStack();
  const showing = Array.isArray(opts.showing)
    ? opts.showing
    : remembered.showing;
  const archive = Array.isArray(opts.archive)
    ? opts.archive
    : remembered.archive;
  const showingIds = new Set(
    showing.map((row) => rowId(row?.id)).filter(Boolean),
  );

  const rewriteAll = orderDirty;
  const mustWrite = (id: string) =>
    rewriteAll || createdIds.has(id) || dirtyIds.has(id);

  try {
    const writes: Promise<unknown>[] = [];

    // Hard-delete soft-removed live rows (complete row removal after Publish).
    const hardDeleteIds = deleted.filter((id) => id && !showingIds.has(id));
    if (hardDeleteIds.length) {
      writes.push(hardDeleteLibRows(userId, hardDeleteIds));
    }

    for (let i = 0; i < showing.length; i += 1) {
      const row = showing[i] as Record<string, unknown>;
      const id = rowId(row?.id);
      if (!id || !mustWrite(id)) continue;
      const local = libStatus(row);
      // Soft-adds become active unless the creator already set draft/archive.
      const isSoftAdd = createdIds.has(id) || isLibPendingPublishStatus(local);
      const activate =
        isSoftAdd && local !== "draft" && local !== "archive";
      const status = activate
        ? "active"
        : isLibPendingPublishStatus(local)
          ? "active"
          : local || "active";
      const index = showing.length - 1 - i;
      writes.push(updateLibRow(userId, id, payloadFromRow(row, index, status)));
    }

    for (let i = 0; i < archive.length; i += 1) {
      const row = archive[i] as Record<string, unknown>;
      const id = rowId(row?.id);
      if (!id || showingIds.has(id) || !mustWrite(id)) continue;
      if (deletedIds.has(id)) continue;
      const local = libStatus(row);
      const status = isLibPendingPublishStatus(local)
        ? "archive"
        : local || "archive";
      writes.push(
        updateLibRow(
          userId,
          id,
          payloadFromRow(row, Number(row.index) || 0, status),
        ),
      );
    }

    await Promise.allSettled(writes);

    createdIds.clear();
    deletedIds.clear();
    dirtyIds.clear();
    sessionDirty = false;
    orderDirty = false;
    lastShowing = [];
    lastArchive = [];
    clearStorage();
    emit(MTE_LIB_SESSION_PUBLISHED);
    return true;
  } catch (err) {
    console.error("[websiteLibPersistGate] publish failed:", err);
    return false;
  }
}
